import React, { useCallback } from 'react';
import { makeStyles, Paper, IconButton, Button, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { usePropValue, inputStateSetterHelper, GenericStatusIndicator, useModePanelStyle, useModeFormStyle, FontIcon, BaseInfoCompProps,
    BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompSaveAction, ModeInfoCompTextInputField } from '@moderepo/mode-app-components';



const useStyle = makeStyles((theme: Theme) => {
    return {
        sensorModuleStatusContainer: {
            marginBottom  : theme.spacing(2),
            display       : 'flex',
            alignItems    : 'center',
            flexDirection : 'row',
            justifyContent: 'space-between',
            width         : '100%',
            fontWeight    : 500,
        },

    };
}, { name: 'SensorModuleInfo' });


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface SCSensorModuleInfoUpdatableInputs {
    readonly name?: string | undefined;
    readonly interval?: number | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface SCSensorModuleInfoActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<SCSensorModuleInfoUpdatableInputs>
}


interface SCSensorModuleInfoProps extends BaseInfoCompProps {
    readonly fields: {
        readonly id: BaseEntityField<string>;
        readonly gatewayId: BaseEntityField<string>;
        readonly name: BaseEntityField<string>;
        readonly modelId: BaseEntityField<string>;
        readonly sensing: BaseEntityField<boolean>;
        readonly interval: BaseEntityField<number>;
        readonly sensors: BaseEntityField<readonly string[]>;
    }

    readonly actions?: SCSensorModuleInfoActionsSet | undefined;
}


/**
 * This is a Sensor Module Info component used for displaying info about a sensor module. Unlike Sensor Module CARD component,
 * this component shows more details and also options for agent to make changes to sensor module.
 */
export const SCSensorModuleInfo: React.FC<SCSensorModuleInfoProps> = (props: SCSensorModuleInfoProps) => {
    const [nameInput, setNameInput] = usePropValue<string | undefined>(props.fields.name.value);
    const [intervalInput, setIntervalInput] = usePropValue<number | undefined>(props.fields.interval.value);


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            name    : setNameInput,
            interval: setIntervalInput,
        });
    }, [setNameInput, setIntervalInput]);



    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setNameInput(props.fields.name.value);
        setIntervalInput(props.fields.interval.value);
    }, [props, setNameInput, setIntervalInput]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();


        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                name    : nameInput?.trim(),
                interval: intervalInput,
            });
        }

        return false;
    }, [props, nameInput, intervalInput]);


    return (

        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >

            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    {props.icon && <FontIcon className={clsx(panelClasses.panelHeaderIcon)} iconName={props.icon} />}
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
                {props.actions?.startEdit && !props.isEditing && (
                    <IconButton onClick={() => {
                        if (props.actions?.startEdit) {
                            props.actions.startEdit.onClick();
                        }
                    }}
                    >
                        <FontIcon iconName={props.actions.startEdit.icon || 'edit'} />
                    </IconButton>
                )}
            </div>
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={clsx(formClasses.formInputGroup)}>
                        {!props.isEditing && !props.fields.sensing.hidden && (
                            <div className={clsx(classes.sensorModuleStatusContainer)}>
                                <div>{props.fields.sensing.label}</div>
                                <GenericStatusIndicator label={props.fields.sensing.displayValue} status={Boolean(props.fields.sensing.value)} />
                            </div>
                        )}
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.id}
                            fieldName="id"
                            inputValue={props.fields.id.displayValue ?? props.fields.id.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.gatewayId}
                            fieldName="gatewayId"
                            inputValue={props.fields.gatewayId.displayValue ?? props.fields.gatewayId.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.modelId}
                            fieldName="modelId"
                            inputValue={props.fields.modelId.displayValue ?? props.fields.modelId.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.name}
                            fieldName="name"
                            inputValue={props.fields.name.editable
                                ? nameInput
                                : props.fields.name.displayValue ?? props.fields.name.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="number"
                            compProps={props}
                            inputProps={props.fields.interval}
                            fieldName="interval"
                            inputValue={props.fields.interval.editable
                                ? intervalInput
                                : props.fields.interval.displayValue ?? props.fields.interval.value}
                            onInputChange={onInputChange}
                        />
                    </div>
                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );
};
